import { lazy, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useStore } from '@hub/helpers/use-store';
import { trackMixpanel } from '@monorepo/client-common';
import { Icons } from '@monorepo/icons';
import { Flex, Helmet, TextComponent } from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import { Application, EventTypes, IconsNames } from '@monorepo/types';
import { RegisterAffiliateModal } from '../shared';
import Carousel from './carousel';
import { CarouselDirection } from './carousel/carousel';
import FrequentlyAskedQuestions from './faq/frequently-asked-questions';
import AffiliationPartnerCard from './partners/affiliation-partner-card';
import ShinyButton from './shiny-button/shiny-button';

import styles from './affiliation-welcome.module.scss';

const project: Application =
  (import.meta.env.VITE_PROJECT as Application) || Application.TTP;

const The5ersBackground = lazy(() => import('./svg/the5ers-circle.svg?react'));

const TTPBackground = lazy(() => import('./svg/ttp-circle.svg?react'));

const AffiliatePartnership = lazy(
  () => import('./svg/affiliate_partnership.svg?react'),
);
const EducationPartnership = lazy(
  () => import('./svg/education_partnership.svg?react'),
);
const InfluencerPartnership = lazy(
  () => import('./svg/influencer_partnership.svg?react'),
);

const AffiliationWelcome = () => {
  const { t } = useTranslation(['affiliate']);
  const {
    dataStore: { userStore },
  } = useStore();
  const { isMobile } = useResponsiveness();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCtaClick = useCallback(() => {
    trackMixpanel({
      event: EventTypes.AFFILIATE_CTA_CLICKED,
      properties: {
        user_id: userStore.currentUser?.userId,
      },
    });
    setIsModalOpen(true);
  }, [userStore]);

  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

  const partnershipMap = useMemo(
    () => [
      {
        icon: AffiliatePartnership,
        title: t('affiliate:welcome.partnership.affiliate_partnership'),
        description: t('affiliate:welcome.partnership.give_chance'),
      },
      {
        icon: InfluencerPartnership,
        title: t('affiliate:welcome.partnership.influencer_partnership'),
        description: t('affiliate:welcome.partnership.earn_with_a_high'),
      },
      {
        icon: EducationPartnership,
        title: t('affiliate:welcome.partnership.educator_partnership'),
        description: t('affiliate:welcome.partnership.reward_your_students'),
      },
    ],
    [t],
  );

  const backgroundMap: Record<
    Application,
    React.LazyExoticComponent<
      React.ComponentType<React.SVGProps<SVGSVGElement>>
    >
  > = {
    [Application.TTP]: TTPBackground,
    [Application.THE5ERS]: The5ersBackground,
  };

  const BackgroundComponent = backgroundMap[project];

  return (
    <>
      <Helmet
        title={`${project === Application.TTP ? 'TTP' : 'The5ers'} Affiliate Program – Earn Commissions Referring Traders`}
      />
      <Flex
        vertical
        className={classNames(styles.affiliationWelcome, {
          [styles.mobile]: isMobile,
        })}
      >
        <Flex className={styles.backgroundWrapper}>
          <BackgroundComponent
            className={classNames(styles.background, {
              [styles.mobile]: isMobile,
            })}
          />
          {isMobile && (
            <BackgroundComponent className={styles.backgroundSecond} />
          )}
        </Flex>
        <Flex
          vertical
          className={classNames(styles.bannerWrapper, {
            [styles.mobile]: isMobile,
          })}
        >
          <Flex
            vertical
            className={classNames(styles.bannerContent, {
              [styles.mobile]: isMobile,
            })}
          >
            <TextComponent
              level={isMobile ? 2 : 3}
              weight={400}
              uppercase
              className={styles.title}
            >
              {t('affiliate:welcome.affiliate_program')}
            </TextComponent>
            <TextComponent level={1} weight={600}>
              {t('affiliate:welcome.converting_is_simpler_part1')}{' '}
              <span className={styles.highlight}>
                {' '}
                {t('affiliate:welcome.converting_is_simpler_part2')}
              </span>{' '}
              {t('affiliate:welcome.converting_is_simpler_part3')}
            </TextComponent>
            <Flex
              className={classNames(styles.buttonWrapper, {
                [styles.mobile]: isMobile,
              })}
            >
              <ShinyButton onClick={handleCtaClick}>
                {t('affiliate:welcome.become_affiliate')}
                <Icons iconName={IconsNames.ChevronRight} />
              </ShinyButton>
            </Flex>
          </Flex>
          <Flex className={styles.partnersWrapper} vertical={isMobile}>
            {partnershipMap.map((partner, index) => (
              <AffiliationPartnerCard key={index} partner={partner} />
            ))}
          </Flex>
        </Flex>
        <Flex vertical className={styles.benefitsWrapper}>
          <TextComponent level={2} weight={600}>
            {t('affiliate:welcome.benefits.earn_more_with_our_benefits')}
          </TextComponent>

          <Flex vertical gap={10}>
            <Carousel direction={CarouselDirection.LEFT} />
            <Carousel direction={CarouselDirection.RIGHT} />
          </Flex>
        </Flex>
        <FrequentlyAskedQuestions />
      </Flex>
      <RegisterAffiliateModal open={isModalOpen} onClose={handleCloseModal} />
    </>
  );
};

export default AffiliationWelcome;
